import React, { useState, useEffect } from 'react';
import { useAuthHeader, useAuthUser } from 'react-auth-kit';
import axios from 'axios';
import PropTypes from "prop-types";
import { Table, Badge, Button, Modal, Form, Spinner, Row, Col } from 'react-bootstrap';
import AddTrips from './PopUps/AddTrips';
import * as Icon from 'react-feather';
import { canPageAction } from '../../config/roleConfig';
import DeactivateConfirmModal from '../Common/DeactivateConfirmModal';
import { mexicoCityNowDateTimeLocal } from '../../utils/dateTime';

function NewTrips(props)  {
    const authHeader = useAuthHeader();
    const authUser = useAuthUser();
    const currentUser = authUser();
    const canRead = canPageAction(currentUser, 'operations.monitoring', 'read');
    const canWrite = canPageAction(currentUser, 'operations.monitoring', 'write');
    const canDelete = canPageAction(currentUser, 'operations.monitoring', 'delete');

    const [data, setData] = useState([]);
    const [expandedTrip, setExpandedTrip] = useState(null);
    const [tripToDeactivate, setTripToDeactivate] = useState(null);
    const [deactivatingTrip, setDeactivatingTrip] = useState(false);
    const [tripToEdit, setTripToEdit] = useState(null);
    const [statusForm, setStatusForm] = useState({
        is_delivered: false,
        date_delivered: '',
        delivered_evidence: false,
        date_delivered_evidence: '',
    });
    const [savingStatus, setSavingStatus] = useState(false);
    const apiUrl = process.env.REACT_APP_URL;

    const get_all_trips = () => {
        axios({
            method:'GET',
            url:apiUrl+'/trips/get_all_trips',
            headers: {
                Authorization: authHeader()
            },
        }).then(response => {
            setData(response.data.msg.Trips);
        })
        .catch(error => {
            alert(error);
            console.log(error);
        });
    }

    const formatDateTimeLocal = (value) => {
        if (!value) {
            return '';
        }
        return value.replace(' ', 'T').slice(0, 16);
    };

    const getMexicoCityDateTimeParts = (value) => {
        if (!value) {
            return { date: '', time: '' };
        }
        const [date = '', time = ''] = formatDateTimeLocal(value).split('T');
        return { date, time };
    };

    const joinDateTimeParts = (date, time) => {
        if (!date || !time) {
            return '';
        }
        return `${date}T${time}`;
    };

    const renderStatusEditor = ({
        checked,
        switchId,
        label,
        accent,
        icon,
        dateTimeValue,
        dateControlId,
        timeControlId,
        dateLabel,
        onToggle,
        onDateTimeChange,
    }) => {
        const { date, time } = getMexicoCityDateTimeParts(dateTimeValue);
        const setDate = (nextDate) => onDateTimeChange(joinDateTimeParts(nextDate, time));
        const setTime = (nextTime) => onDateTimeChange(joinDateTimeParts(date, nextTime));

        return (
            <div
                className="mb-4"
                style={{
                    border: checked ? `1px solid ${accent}` : '1px solid #e9ecef',
                    borderRadius: 6,
                    overflow: 'hidden',
                    background: '#fff',
                    boxShadow: checked ? '0 8px 22px rgba(0, 0, 0, 0.06)' : 'none',
                }}
            >
                <div
                    className="d-flex justify-content-between align-items-center px-4 py-3"
                    style={{
                        background: checked ? `${accent}22` : '#f8f9fa',
                        borderBottom: '1px solid #e9ecef',
                    }}
                >
                    <div className="d-flex align-items-center gap-2">
                        <span style={{ color: accent }}>{icon}</span>
                        <div>
                            <div className="fw-semibold" style={{ color: checked ? accent : '#495057' }}>
                                {label}
                            </div>
                            <small className="text-muted">Hora de Ciudad de Mexico</small>
                        </div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <Badge bg={checked ? 'success' : 'secondary'}>{checked ? 'Completado' : 'Pendiente'}</Badge>
                        <Form.Check
                            type="switch"
                            id={switchId}
                            checked={checked}
                            disabled={savingStatus}
                            onChange={(event) => onToggle(event.target.checked)}
                        />
                    </div>
                </div>
                {checked && (
                    <div className="px-4 py-4">
                        <Row>
                        <Col md={7} className="mb-3 mb-md-0">
                            <Form.Group controlId={dateControlId}>
                                <Form.Label>{dateLabel}</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={date}
                                    disabled={savingStatus}
                                    onChange={(event) => setDate(event.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={5}>
                            <Form.Group controlId={timeControlId}>
                                <Form.Label>Hora</Form.Label>
                                <Form.Control
                                    type="time"
                                    value={time}
                                    disabled={savingStatus}
                                    onChange={(event) => setTime(event.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        </Row>
                    </div>
                )}
            </div>
        );
    };

    const updateDeliveredStatus = (id, delivered, column, deliveredAt = null) => {
        const data = {
            id: id,
            delivered: delivered,
            column: column
        };
        if (delivered && deliveredAt) {
            data.delivered_at = deliveredAt;
        }

        return axios({
            method:'PUT',
            url:apiUrl+'/trips/update_delivered',
            headers: {
                Authorization: authHeader()
            },
            data: data
        });
    }

    const handleDeliveredClick = (id, delivered, column, current_status) => {
        if (current_status !== delivered) {
            updateDeliveredStatus(id, delivered, column).then(response => {
                get_all_trips();
            })
            .catch(error => {
                alert(error);
                console.log(error);
            });
        }
    }

    useEffect(() => {
        get_all_trips();
    }, []);

    const toggleTripDetails = (tripId) => {
        setExpandedTrip(expandedTrip === tripId ? null : tripId);
    };

    const openEditStatusModal = (trip) => {
        setTripToEdit(trip);
        setStatusForm({
            is_delivered: Boolean(trip.is_delivered),
            date_delivered: formatDateTimeLocal(trip.date_delivered),
            delivered_evidence: Boolean(trip.delivered_evidence),
            date_delivered_evidence: formatDateTimeLocal(trip.date_delivered_evidence),
        });
    };

    const closeEditStatusModal = () => {
        if (!savingStatus) {
            setTripToEdit(null);
        }
    };

    const saveTripStatuses = () => {
        if (!tripToEdit) {
            return;
        }

        const updates = [];
        const deliveredDateChanged = formatDateTimeLocal(tripToEdit.date_delivered) !== statusForm.date_delivered;
        const evidenceDateChanged = formatDateTimeLocal(tripToEdit.date_delivered_evidence) !== statusForm.date_delivered_evidence;

        if (statusForm.is_delivered && !statusForm.date_delivered) {
            alert('Capture la fecha y hora de finalizacion del viaje');
            return;
        }
        if (statusForm.delivered_evidence && !statusForm.date_delivered_evidence) {
            alert('Capture la fecha y hora de entrega de evidencias');
            return;
        }

        if (Boolean(tripToEdit.is_delivered) !== statusForm.is_delivered || (statusForm.is_delivered && deliveredDateChanged)) {
            updates.push(updateDeliveredStatus(tripToEdit.id, statusForm.is_delivered, "delivered", statusForm.date_delivered));
        }
        if (Boolean(tripToEdit.delivered_evidence) !== statusForm.delivered_evidence || (statusForm.delivered_evidence && evidenceDateChanged)) {
            updates.push(updateDeliveredStatus(tripToEdit.id, statusForm.delivered_evidence, "delivered_evidence", statusForm.date_delivered_evidence));
        }

        if (updates.length === 0) {
            setTripToEdit(null);
            return;
        }

        setSavingStatus(true);
        Promise.all(updates).then(() => {
            setTripToEdit(null);
            get_all_trips();
        }).catch(error => {
            alert(error.response?.data?.error || 'No se pudo actualizar el estatus del viaje');
            console.log(error);
        }).finally(() => {
            setSavingStatus(false);
        });
    };

    const deleteTrip = (trip) => {
        if (!trip) {
            return;
        }

        setDeactivatingTrip(true);
        axios({
            method: 'DELETE',
            url: apiUrl + '/trips/delete',
            headers: {
                Authorization: authHeader()
            },
            data: { id: trip.id }
        }).then(() => {
            setTripToDeactivate(null);
            get_all_trips();
        }).catch(error => {
            alert(error.response?.data?.error || 'No se pudo desactivar el viaje');
            console.log(error);
        }).finally(() => {
            setDeactivatingTrip(false);
        });
    };

        return (
            <>
            <div className="card mb-4">
                <div className="card-body">
                    <div className="card-header d-flex justify-content-between">
                        <h5 className="card-title">Viajes</h5>
                    </div>

                    {/* <div className="height-310"> */}
                    <div>
                        <Table className="m-0" responsive>
                            <thead>
                                <tr>
                                    <th>No. Factura</th>
                                    <th>Cliente</th>
                                    <th>Paradas</th>
                                    <th>Maniobras</th>
                                    <th>Estadías</th>
                                    <th>Operador</th>
                                    <th>Unidad</th>
                                    <th>Inicio</th>
                                    <th>Viaje Finalizado</th>
                                    <th>Estatus de Evidencias</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
        {data.map(trip => (
            <React.Fragment key={trip.id}>
                <tr>
                    <td>{trip.invoice_id}</td>
                    <td>{trip.company}</td>
                    <td>{trip.stops.length}</td>
                    <td>0</td>
                    <td>0</td>
                    <td>{trip.operator}</td>
                    <td>{trip.inventory}</td>
                    <td>{trip.stops[0].origin_date}</td>
                    <td>{trip.date_delivered}
                        {canWrite && (
                            <Button size="sm" variant={trip.is_delivered ? "outline-success" : "outline-danger"} className="ms-1" onClick={() => {
                                handleDeliveredClick(trip.id, true, "delivered", trip.is_delivered);
                            }}>
                                <Icon.Check size={14} />
                            </Button>
                        )}
                    </td>
                    <td>{trip.date_delivered_evidence}
                        {canWrite && (
                            <Button size="sm" variant={trip.delivered_evidence ? "outline-success" : "outline-danger"} className="ms-1" onClick={() => {
                                handleDeliveredClick(trip.id, true, "delivered_evidence", trip.delivered_evidence);
                            }}>
                                <Icon.Check size={14} />
                            </Button>
                        )}
                    </td>
                    <td>
                        <div className="d-flex gap-1">
                            {canRead && (
                                <Button
                                    size="sm"
                                    variant="outline-primary"
                                    onClick={() => toggleTripDetails(trip.id)}
                                >
                                    <Icon.Eye size={14} />
                                </Button>
                            )}
                            {canWrite && (
                                <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    onClick={() => openEditStatusModal(trip)}
                                >
                                    <Icon.Edit2 size={14} />
                                </Button>
                            )}
                            {canDelete && (
                                <Button
                                    size="sm"
                                    variant="outline-danger"
                                    onClick={() => setTripToDeactivate(trip)}
                                >
                                    <Icon.Trash2 size={14} />
                                </Button>
                            )}
                            {!canRead && !canWrite && !canDelete && (
                                <span className="text-muted">N/A</span>
                            )}
                        </div>
                        </td>
                </tr>
                {expandedTrip === trip.id && (
                    <tr>
                        <td colSpan="11">
                            <div className="p-3 bg-light">
                                <div className="row">
                                    <div className="col-md-4">
                                        <p><strong>Cliente:</strong> {trip.company || 'N/A'}</p>
                                        <p><strong>Factura:</strong> {trip.invoice_id || 'N/A'}</p>
                                    </div>
                                    <div className="col-md-4">
                                        <p><strong>Operador:</strong> {trip.operator || 'N/A'}</p>
                                        <p><strong>Unidad:</strong> {trip.inventory || 'N/A'}</p>
                                    </div>
                                    <div className="col-md-4">
                                        <p><strong>Inicio:</strong> {trip.stops?.[0]?.origin_date || 'N/A'}</p>
                                        <p><strong>Paradas:</strong> {trip.stops?.length || 0}</p>
                                    </div>
                                </div>
                            </div>
                        </td>
                    </tr>
                )}
            </React.Fragment>
        ))}
    </tbody>
                        </Table>
                    </div>
                </div>
            </div>
            <DeactivateConfirmModal
                show={Boolean(tripToDeactivate)}
                title="Desactivar Viaje"
                itemLabel={tripToDeactivate?.invoice_id || `Viaje ${tripToDeactivate?.id}`}
                body="Este viaje se desactivará y dejará de aparecer en los registros principales. La información relacionada se conservará."
                confirming={deactivatingTrip}
                onCancel={() => !deactivatingTrip && setTripToDeactivate(null)}
                onConfirm={() => deleteTrip(tripToDeactivate)}
            />
            <Modal show={Boolean(tripToEdit)} onHide={closeEditStatusModal} centered size="lg">
                <Modal.Header closeButton={!savingStatus} className="px-4 py-3">
                    <Modal.Title className="fw-bold">Editar Viaje</Modal.Title>
                </Modal.Header>
                <Modal.Body className="px-4 py-4">
                    <div style={{ width: 48, height: 2, background: '#2962ff', marginBottom: 24 }} />
                    <div
                        className="px-4 py-3 mb-4"
                        style={{
                            background: '#e7f0ff',
                            border: '1px solid #b7d0ff',
                            borderRadius: 6,
                            color: '#0d3975',
                        }}
                    >
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <div className="fw-semibold">
                                    {tripToEdit?.invoice_id ? `Factura ${tripToEdit.invoice_id}` : `Viaje ${tripToEdit?.id || ''}`}
                                </div>
                                <small>{tripToEdit?.company || 'Cliente no disponible'}</small>
                            </div>
                            <Icon.Truck size={28} />
                        </div>
                    </div>
                    <Form>
                        {renderStatusEditor({
                            checked: statusForm.is_delivered,
                            switchId: 'trip-delivered-switch',
                            label: 'Viaje finalizado',
                            accent: '#2962ff',
                            icon: <Icon.MapPin size={22} />,
                            dateTimeValue: statusForm.date_delivered,
                            dateControlId: 'trip-delivered-date',
                            timeControlId: 'trip-delivered-time',
                            dateLabel: 'Fecha de finalizacion',
                            onToggle: (checked) => setStatusForm({
                                ...statusForm,
                                is_delivered: checked,
                                date_delivered: checked ? (statusForm.date_delivered || mexicoCityNowDateTimeLocal()) : '',
                            }),
                            onDateTimeChange: (value) => setStatusForm({
                                ...statusForm,
                                date_delivered: value,
                            }),
                        })}
                        {renderStatusEditor({
                            checked: statusForm.delivered_evidence,
                            switchId: 'trip-evidence-switch',
                            label: 'Evidencias entregadas',
                            accent: '#198754',
                            icon: <Icon.Flag size={22} />,
                            dateTimeValue: statusForm.date_delivered_evidence,
                            dateControlId: 'trip-evidence-date',
                            timeControlId: 'trip-evidence-time',
                            dateLabel: 'Fecha de entrega',
                            onToggle: (checked) => setStatusForm({
                                ...statusForm,
                                delivered_evidence: checked,
                                date_delivered_evidence: checked ? (statusForm.date_delivered_evidence || mexicoCityNowDateTimeLocal()) : '',
                            }),
                            onDateTimeChange: (value) => setStatusForm({
                                ...statusForm,
                                date_delivered_evidence: value,
                            }),
                        })}
                    </Form>
                </Modal.Body>
                <Modal.Footer className="px-4 py-3">
                    <Button variant="secondary" onClick={closeEditStatusModal} disabled={savingStatus}>
                        Cancelar
                    </Button>
                    <Button variant="primary" onClick={saveTripStatuses} disabled={savingStatus}>
                        {savingStatus ? (
                            <>
                                <Spinner as="span" animation="border" size="sm" role="status" className="me-1" />
                                Guardando...
                            </>
                        ) : 'Guardar'}
                    </Button>
                </Modal.Footer>
            </Modal>
            </>
        );

}

//Status: Entregado, Pendiente, Cancelado, En camino
// Success, Warning, Danger, Info
//https://react-bootstrap.netlify.app/docs/components/badge/
export default NewTrips;
